import React from 'react';
import { useQuery } from '@tanstack/react-query';
import postService, { Post } from '../../services/postService';
import { useDragStore } from '../../stores/dragStore';
import { useClient } from '../../contexts/ClientContext';
import AuthenticatedImage from '../media/AuthenticatedImage';
import { Loader2, FileText, Grip, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';

const ContentQueue: React.FC = () => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['scheduledPostsForQueue'],
    queryFn: () => postService.getPosts(1, 5, 'scheduled'), // Fetching up to 5 scheduled posts
  });
  const setDraggedPost = useDragStore((state) => state.setDraggedPost);
  const { currentClient } = useClient();

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, post: Post) => {
    setDraggedPost(post);
  };

  return (
    <div className="relative">
      <div className="absolute -inset-1 bg-gradient-to-r from-indigo-500/10 via-purple-500/10 to-pink-500/10 rounded-2xl blur"></div>
      <div className="relative bg-white dark:bg-slate-800/40 backdrop-blur-xl p-6 rounded-2xl border border-slate-200/50 dark:border-slate-600/30 shadow-xl">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #30b3dd 0%, #4a298a 100%)' }}>
            <FileText className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-900 dark:text-white">Content Queue</h2>
            <p className="text-sm text-slate-500 dark:text-slate-400">Drag posts to schedule</p>
          </div>
        </div>

        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="w-12 h-12 border-2 border-slate-300 dark:border-slate-600 border-t-indigo-500 rounded-full animate-spin mb-4"></div>
            <p className="text-slate-500 dark:text-slate-400">Loading queue...</p>
          </div>
        ) : error ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <FileText className="w-8 h-8 text-red-400" />
            </div>
            <p className="text-red-500 dark:text-red-400 font-medium">Error loading drafts</p>
          </div>
        ) : data?.posts.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-20 h-20 bg-slate-200/50 dark:bg-slate-700/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <FileText className="w-10 h-10 text-slate-500 dark:text-slate-500" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-300 mb-2">No posts in queue</h3>
            <p className="text-slate-500 dark:text-slate-400">Create some posts to get started</p>
          </div>
        ) : (
          <div className="space-y-3">
            {data?.posts.map((post, index) => (
              <motion.div
                key={post.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                draggable
                onDragStart={(e) => handleDragStart(e, post)}
                className="group p-4 bg-white dark:bg-slate-700/30 hover:bg-slate-100 dark:hover:bg-slate-700/50 rounded-xl cursor-move flex items-center space-x-4 border border-slate-200/50 dark:border-slate-600/30 hover:border-indigo-500/50 transition-all duration-300 hover:shadow-lg hover:shadow-indigo-500/10"
              >
                <div className="flex-shrink-0">
                  <Grip className="w-5 h-5 text-slate-500 dark:text-slate-400 group-hover:text-indigo-500 dark:group-hover:text-indigo-400 transition-colors" />
                </div>
                
                {post.media_files && post.media_files.length > 0 && currentClient ? (
                  <div className="w-14 h-14 flex-shrink-0 rounded-xl overflow-hidden border border-slate-200/50 dark:border-slate-600/50">
                    <AuthenticatedImage
                      src={`/api/v1/media/${post.media_files[0]}/thumbnail`}
                      alt={post.title}
                      className="w-full h-full object-cover"
                      clientId={currentClient.id}
                    />
                  </div>
                ) : (
                  <div className="w-14 h-14 flex-shrink-0 bg-slate-100 dark:bg-slate-600/30 rounded-xl flex items-center justify-center">
                    <FileText className="w-6 h-6 text-slate-500 dark:text-slate-400" />
                  </div>
                )}
                
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-slate-900 dark:text-white truncate group-hover:text-indigo-600 dark:group-hover:text-indigo-200 transition-colors">
                    {post.title}
                  </p>
                  <p className="text-sm text-slate-500 dark:text-slate-400 truncate mt-1">
                    {post.content}
                  </p>
                </div>
                
                <div className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Sparkles className="w-4 h-4 text-indigo-500 dark:text-indigo-400" />
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ContentQueue;